import { type Blueprint } from '@rta/rta-blueprint-library';

import { BlueprintCustomPanelCard } from '../shared/blueprint-custom-panel-card';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { PAGE_BODY_PADDING } from '../shared/blueprint-view-styles';
import { useBlueprintCustomPages } from '../shared/blueprint-custom-pages-context';
import type { BlueprintPage } from '../types';

export type BlueprintCustomPanelPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

/**
 * A blank user-added sheet. Same header / body / footer spacing as every panel sheet, but its
 * body holds only the free-standing rectangles the user adds by right-clicking the page.
 */
export const BlueprintCustomPanelPage = (props: BlueprintCustomPanelPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const { customPages } = useBlueprintCustomPages();
	const customPage = customPages.find((candidate) => candidate.id === page.customPageId);

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', clipPath: 'inset(0)', minHeight: 0 }}>
				<div style={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
					minHeight: 0,
					padding: PAGE_BODY_PADDING
				}}>
					{customPage && (
						<BlueprintCustomPanelCard
							blueprint={blueprint}
							pageId={customPage.id}
							panels={customPage.panels}
						/>
					)}
				</div>
			</div>

			<div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
				<BlueprintPanelFooter scope={page.overrideScope ?? `custom-panel.${page.customPageId}`} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
